/**
 * kult-send.js — Formulare ohne Seitenwechsel an die Kult-App senden (App-Proxy /apps/seminare/…).
 * Hintergrund (Okt. 2026): Shopifys Kontaktformular (POST /contact) lief je nach Bot-Schutz auf eine weiße Prüfseite.
 * Anfragen und Newsletter-Anmeldungen gehen darum als JSON an die eigene App; die App verschickt die Mail an die
 * Shop-Adresse bzw. die Bestätigungs-Mail (Double-Opt-in). Kein Seitenwechsel, Erfolg und Fehler erscheinen im Formular.
 *
 * 1) Funktion  window.KultSend.post(endpoint, payload) → Promise<{ ok, status, error, data }>   (wirft nie, 20 s Zeitlimit)
 *    Header: Content-Type/Accept application/json, x-requested-with: kult-form (für /b2b-anfrage: kult-b2b, 3. Argument).
 * 2) Deklarativ: <form data-kult-send data-endpoint="/apps/seminare/kontakt" novalidate>
 *      Felder mit name="…" werden als { name: wert } gesendet (Checkbox: true/false, leere Texte als ""),
 *      dazu page = location.pathname. data-send-extra='{"quelle":"footer"}' ergänzt feste Werte.
 *      [data-send-consent] (Element): dessen Text geht als consentText mit.
 *      [data-send-error]   (Element, role="alert", hidden): Fehlermeldung.
 *      [data-send-success] (Element, role="status", hidden, tabindex="-1"): Erfolgsmeldung, bekommt den Fokus.
 *      [data-send-fields]  (Element, optional): wird nach Erfolg ausgeblendet (sonst alle Kinder außer Erfolg).
 *      Pflichtfelder: native Prüfung (required, type=email); Meldungen in [data-send-error], Fokus aufs erste Feld.
 *      data-send-invalid="…" überschreibt den Standardtext für unvollständige Eingaben.
 *    Ereignisse am Formular (bubbles): "kult:send:success" und "kult:send:error" (detail { error }).
 * Honigtopf: ein Feld name="hp" (per CSS versteckt, tabindex -1, autocomplete off) geht mit; die App verwirft Treffer still.
 */
(function () {
  'use strict';
  if (window.KultSend) return;

  var TIMEOUT_MS = 20000;
  var MSG_FAIL = 'Das hat gerade nicht geklappt. Bitte gleich noch einmal versuchen.';
  var MSG_INVALID = 'Bitte die markierten Felder ausfüllen.';

  function post(endpoint, payload, requestedWith) {
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = window.setTimeout(function () { if (controller) controller.abort(); }, TIMEOUT_MS);
    return fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'x-requested-with': requestedWith || 'kult-form' },
      body: JSON.stringify(payload),
      credentials: 'same-origin',
      signal: controller ? controller.signal : undefined
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = JSON.parse(text); } catch (e) { data = null; }
        var ok = res.ok && !!data && data.ok !== false;
        return { ok: ok, status: res.status, error: ok ? '' : ((data && data.error) || MSG_FAIL), data: data };
      });
    }).catch(function () {
      return { ok: false, status: 0, error: MSG_FAIL, data: null };
    }).then(function (result) { window.clearTimeout(timer); return result; });
  }

  function payloadOf(form) {
    var out = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      if (!el.name || el.disabled || el.type === 'submit' || el.type === 'button') return;
      if (el.type === 'checkbox') { out[el.name] = !!el.checked; return; }
      if (el.type === 'radio') { if (el.checked) out[el.name] = el.value; return; }
      out[el.name] = String(el.value == null ? '' : el.value).trim();
    });
    try { var extra = JSON.parse(form.getAttribute('data-send-extra') || '{}'); Object.keys(extra).forEach(function (k) { out[k] = extra[k]; }); } catch (e) { /* ohne Zusatzwerte */ }
    var consent = form.querySelector('[data-send-consent]');
    if (consent) out.consentText = consent.textContent.replace(/\s+/g, ' ').trim().slice(0, 1000);
    out.page = location.pathname;
    return out;
  }

  function showError(form, text) {
    var box = form.querySelector('[data-send-error]');
    if (!box) return;
    var target = box.querySelector('[data-send-error-text]') || box;
    target.textContent = text || '';
    box.hidden = !text;
  }

  function onSubmit(event) {
    var form = event.target;
    if (!form || !form.matches || !form.matches('form[data-kult-send]')) return;
    event.preventDefault();
    if (form.getAttribute('aria-busy') === 'true') return;
    showError(form, '');
    if (window.KultNotify) window.KultNotify.clearForm(form);
    var fields = Array.prototype.filter.call(form.elements, function (el) { return el.willValidate; });
    var invalid = fields.filter(function (el) { return !el.validity.valid; });
    fields.forEach(function (el) { if (el.validity.valid) el.removeAttribute('aria-invalid'); else el.setAttribute('aria-invalid', 'true'); });
    if (invalid.length) {
      var invalidText = form.getAttribute('data-send-invalid') || MSG_INVALID;
      /* Meldeleiste + Fehlertext am Feld + Scroll zum ersten Feld (14.10.2026); Inline-Box nur ohne Leiste,
         sonst sagen zwei role=alert dieselbe Meldung doppelt an (Audit 14.10., WCAG 4.1.3) */
      if (window.KultNotify) window.KultNotify.formError(form, invalid, invalidText);
      else { showError(form, invalidText); invalid[0].focus(); }
      return;
    }
    var button = form.querySelector('[type="submit"]');
    form.setAttribute('aria-busy', 'true');
    if (button) button.disabled = true;
    post(form.getAttribute('data-endpoint'), payloadOf(form)).then(function (result) {
      form.removeAttribute('aria-busy');
      if (button) button.disabled = false;
      if (!result.ok) {
        if (window.KultNotify) window.KultNotify.error(result.error);
        else showError(form, result.error);
        /* Fokus nicht am Seitenanfang verlieren: zurück auf den Absenden-Knopf (Audit 14.10., WCAG 2.4.3) */
        if (button) { try { button.focus({ preventScroll: true }); } catch (e) { button.focus(); } }
        form.dispatchEvent(new CustomEvent('kult:send:error', { bubbles: true, detail: { error: result.error } }));
        return;
      }
      var success = form.querySelector('[data-send-success]');
      var fieldsBox = form.querySelector('[data-send-fields]');
      if (fieldsBox) fieldsBox.hidden = true;
      else Array.prototype.forEach.call(form.children, function (child) { if (child !== success && !(success && child.contains(success))) child.hidden = true; });
      if (success) { success.hidden = false; try { success.focus({ preventScroll: false }); } catch (e) { success.focus(); } }
      /* silent: die fokussierte Erfolgsbox (role=status) sagt den Text schon an (Audit 14.10.) */
      if (window.KultNotify && success) window.KultNotify.success(success.textContent.replace(/\s+/g, ' ').trim(), { silent: true });
      form.dispatchEvent(new CustomEvent('kult:send:success', { bubbles: true, detail: {} }));
    });
  }

  document.addEventListener('submit', onSubmit);
  document.addEventListener('input', function (event) {
    var el = event.target;
    if (!(el && el.form && el.form.matches && el.form.matches('form[data-kult-send]') && el.validity && el.validity.valid)) return;
    /* korrigiertes Feld: Markierung UND Fehlertext entfernen (Audit 14.10., WCAG 3.3.1) */
    if (window.KultNotify) window.KultNotify.clearField(el);
    else el.removeAttribute('aria-invalid');
  });

  window.KultSend = { post: post, MSG_FAIL: MSG_FAIL };
})();
